import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { Address, AttestationTemplate, AttestationTemplateRepositoryService, Brand, CorporationRepositoryService, eIdentityVerificationSource, Order, OrderManagerService, Utilities, ValidationProblem, ValidationService } from 'reg-hub-common';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-ultimate-beneficial-ownership-report-review',
  templateUrl: './ultimate-beneficial-ownership-report-review.component.html',
  styleUrl: './ultimate-beneficial-ownership-report-review.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UltimateBeneficialOwnershipReportReviewComponent extends SteppedComponent {
  @Input() printMode: boolean = false;
  attestationTemplate: AttestationTemplate | undefined;
  brand: Brand | undefined;
  brandLogoUrl: string | null = null;
  displayedDocColumns = ['name', 'type'];

  constructor(orderManager: OrderManagerService,
    validationService: ValidationService,
    private riskTemplateRepo: AttestationTemplateRepositoryService,
    private corporationRepo: CorporationRepositoryService) {
    super(orderManager, validationService);
  }

  protected override init(order: Order): void {
    super.init(order);

    this.validationService.currentValidation$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(validation => this.validation$.next(validation));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    var templateID = this.order.ultimateBeneficialOwnershipRequest?.templateID;
    if (templateID) {
      this.riskTemplateRepo.getAttestationTemplate(templateID).subscribe(template => {
        this.attestationTemplate = template;
      });
    }

    var brandID = this.order.ultimateBeneficialOwnershipRequest?.brandID;
    var corporationID = this.order.orderGroup?.corporationID;
    if (brandID && corporationID) {
      const corpId = corporationID;
      this.corporationRepo.getBrand(corpId, brandID).subscribe({
        next: brand => {
          this.brand = brand ?? undefined;
          if (brand?.logoDocumentID) {
            this.loadBrandLogo(corpId, brand.id);
          }
        },
        error: () => this.brand = undefined
      });
    }
  }

  private loadBrandLogo(corporationID: string, brandID: string): void {
    this.corporationRepo.getBrandDocument(corporationID, brandID).subscribe({
      next: doc => {
        if (doc?.base64) {
          const type = `${doc.documentTypeID}`.toUpperCase();
          const mime = (type === 'JPG' || type === 'JPEG' || type === '9') ? 'image/jpeg' : 'image/png';
          this.brandLogoUrl = `data:${mime};base64,${doc.base64}`;
        }
      },
      error: () => this.brandLogoUrl = null
    });
  }

  override ngOnDestroy(): void {
    this.validationService.clearValidation();
    super.ngOnDestroy();
  }

  public override onSaving(): void {

  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {

  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return (errors?.length ?? 0) === 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
  }

  // ------- Derived getters & helpers -------

  get sortedRiskQuestions() {
    const items = this.attestationTemplate?.attestationTemplateFinancialRiskQuestions ?? [];
    // Defensive copy before sort
    return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  get supportingDocs() {
    return this.attestationTemplate?.attestationTemplateSupportingBusinessDocuments ?? [];
  }

  displayBirthday(): string {
    if (this.order.parties?.at(0)?.dateOfBirth) {
      return new Date(this.order.parties!.at(0)!.dateOfBirth!.toString()).toISOString().slice(0, 10);
    }
    return '';
  }

  displayAddress(address: Address): string {
    return `${address.unit ? address.unit + "-" : ''}${address.streetNumber ?? ''} ${address.streetName ?? ''}, ${address.city ?? ''}, ${address.jurisdiction ?? ''}, ${address.countryCode ?? ''}, ${address.postalCode ?? ''}`;
  }

  // Display utilities that tolerate different field names
  displayQuestion(q: any): string {
    if (!q) return '(question unavailable)';
    return q.question;
  }

  displayOption(opt: any): string {
    if (!opt) return '';
    return opt.option;
  }

  displayDocName(doc: any): string {
    const base = doc?.supportingBusinessDocument;
    return base.name;
  }

  displayDocType(doc: any): string {
    const base = doc?.supportingBusinessDocument;
    const raw = base?.documentType;
    return `${raw ?? ''}`;
  }

  showEmail(): boolean {
    return this.order.ultimateBeneficialOwnershipRequest!.sendEmail;
  }

  attestorDetailedInformationVisible(): boolean {
    return (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource) ?? false) ||
      (this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau) ?? false);
  }

  attestationRequestorDetailsVisible(): boolean {
    return this.order.ultimateBeneficialOwnershipRequest!.displayedCorpName != null;
  }

  showBrand(): boolean {
    return this.brand != null;
  }

  mapIdRequirementString(): string {
    const idvSources = this.order.identityVerificationDetails?.sources ?? [];
    let stringSources: string[] = [];
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxAmlSingleSource));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.TuCreditBureau));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxDvs));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EntrustDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EntrustDvs));
    }
    return stringSources.join(', ');
  }

  showTextMessageInFrench(): boolean {
    return this.order.identityVerificationDetails?.sources.some(s => s.name == eIdentityVerificationSource.EfxDvs) ?? false;
  }
}
